// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Image, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { INSURANCE_CARD_DOCUMENT_TYPE_SYSTEM, INSURANCE_CARD_SIDES } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { type JSX, useEffect, useState } from 'react';

interface CardPhoto {
  title: string;
  url: string;
}

const SIDES = Object.values(INSURANCE_CARD_SIDES);

function cardSide(document: DocumentReference): (typeof SIDES)[number] | undefined {
  const coding = document.type?.coding?.find((entry) => entry.system === INSURANCE_CARD_DOCUMENT_TYPE_SYSTEM);
  return SIDES.find((side) => side.code === coding?.code);
}

/** Front and back insurance card photos linked to a Coverage, as saved by intake. */
export function InsuranceCardPhotos({ coverageId }: { coverageId: string }): JSX.Element {
  const medplum = useMedplum();
  const [photos, setPhotos] = useState<CardPhoto[]>();

  useEffect(() => {
    let disposed = false;
    const objectUrls: string[] = [];
    Sentry.startSpan({ op: 'http.client', name: 'Load insurance card photos' }, async () => {
      const documents = await medplum.searchResources('DocumentReference', { related: `Coverage/${coverageId}` });
      const cards = documents
        .flatMap((document: DocumentReference) => {
          const side = cardSide(document);
          return side ? [{ document, side }] : [];
        })
        .sort((a, b) => SIDES.indexOf(a.side) - SIDES.indexOf(b.side));
      return Promise.all(
        cards.map(async ({ document, side }) => {
          const attachmentUrl = document.content?.[0]?.attachment?.url ?? '';
          const blob = await medplum.download(attachmentUrl);
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          return { title: side.title, url };
        })
      );
    })
      .then((loaded) => {
        if (!disposed) {
          setPhotos(loaded);
        }
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        if (!disposed) {
          setPhotos([]);
        }
      });
    return () => {
      disposed = true;
      for (const url of objectUrls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [medplum, coverageId]);

  if (!photos) {
    return <Loader size="xs" aria-label="Loading card photos" />;
  }
  if (photos.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No card photos on file.
      </Text>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {photos.map((photo) => (
        <Stack key={photo.title} gap={4}>
          <Anchor href={photo.url} target="_blank" rel="noopener noreferrer">
            <Image src={photo.url} alt={photo.title} radius="sm" mah={200} fit="contain" />
          </Anchor>
          <Text size="xs" c="dimmed">
            {photo.title}
          </Text>
        </Stack>
      ))}
    </SimpleGrid>
  );
}
